// Web-only Google Identity Services (GIS) helper.
//
// Returns a Google ID token the backend (/v1/auth/google) can verify, or
// null when Google sign-in can't run here — no `window` (native RN), no
// configured client id, or the user dismissed the prompt. Native Google
// Sign-In needs a separate native SDK + config, so on phones the caller
// falls back to email auth.
//
// The client id is the OAuth *Web* client id from Google Cloud Console,
// exposed to the browser as NEXT_PUBLIC_GOOGLE_CLIENT_ID (see next.config).
// It must match the backend's GOOGLE_CLIENT_ID (same value) — that's what
// the ID token's audience is verified against.

import { getApiBaseUrl } from "../api/client";

// `?hl=en` pins the GIS widget's language. GIS locks its locale at script
// load time from this param (falling back to the browser locale otherwise),
// so the button config `locale` alone isn't enough — the whole UI-only app
// is English, so the Google button must be too.
const GIS_SRC = "https://accounts.google.com/gsi/client?hl=en";

// The client id comes from the backend (GET /v1/auth/google/config) so the
// single source of truth is the server's GOOGLE_CLIENT_ID env — no fragile
// build-time env inlining. An optional NEXT_PUBLIC_GOOGLE_CLIENT_ID / window
// global is honored as a fast-path fallback if present.
let clientIdCache: string | null | undefined; // undefined = not fetched yet

function envClientId(): string | undefined {
  // Plain dot access so Next's DefinePlugin can inline it when set; harmless
  // (undefined) on native RN / when unset.
  const fromEnv = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID as
    | string
    | undefined;
  const fromWindow =
    typeof window !== "undefined"
      ? ((window as any).__GOOGLE_CLIENT_ID as string | undefined)
      : undefined;
  return fromEnv || fromWindow || undefined;
}

async function getClientId(): Promise<string | null> {
  if (clientIdCache !== undefined) return clientIdCache;
  const fast = envClientId();
  if (fast) {
    clientIdCache = fast;
    return fast;
  }
  try {
    const res = await fetch(`${getApiBaseUrl()}/v1/auth/google/config`);
    const json = (await res.json()) as { clientId?: string | null };
    clientIdCache = json?.clientId ?? null;
  } catch {
    clientIdCache = null;
  }
  return clientIdCache;
}

let scriptPromise: Promise<void> | null = null;
function loadGis(): Promise<void> {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return Promise.reject(new Error("Google sign-in is web-only"));
  }
  if ((window as any).google?.accounts?.id) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<void>((resolve, reject) => {
    const s = document.createElement("script");
    s.src = GIS_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Failed to load Google sign-in"));
    document.head.appendChild(s);
  });
  return scriptPromise;
}

// Render Google's OFFICIAL Sign-In button into `el`. This is the reliable
// path — clicking it opens the account chooser as a popup (no One-Tap
// suppression / FedCM / cooldown flakiness) and the callback fires with an
// ID token the backend already knows how to verify. Returns false when
// Google sign-in isn't available (no client id / not web) so the caller can
// show a fallback. `width` is clamped to GIS's 200–400px range.
export async function renderGoogleButton(
  el: HTMLElement,
  onToken: (idToken: string) => void,
  width?: number,
): Promise<boolean> {
  if (typeof window === "undefined") return false;
  const clientId = await getClientId();
  if (!clientId) return false;
  try {
    await loadGis();
  } catch {
    return false;
  }
  const google = (window as any).google;
  if (!google?.accounts?.id) return false;

  google.accounts.id.initialize({
    client_id: clientId,
    ux_mode: "popup",
    callback: (resp: { credential?: string }) => {
      if (resp?.credential) onToken(resp.credential);
    },
  });
  google.accounts.id.renderButton(el, {
    type: "standard",
    theme: "outline",
    size: "large",
    text: "continue_with",
    shape: "rectangular",
    logo_alignment: "left",
    // Pin the button to English so it matches the rest of the (English-only)
    // UI. Without this GIS renders in the browser/session locale.
    locale: "en",
    ...(width && width > 0
      ? { width: Math.min(400, Math.max(200, Math.floor(width))) }
      : {}),
  });
  return true;
}

// Resolves true when Google sign-in can actually run here (web + a client
// id configured on the backend). Used to decide between the real flow and
// the "use email" fallback.
export async function isGoogleConfigured(): Promise<boolean> {
  if (typeof window === "undefined") return false;
  return !!(await getClientId());
}

// Trigger the Google account chooser and resolve with an ID token, or null
// if it couldn't be shown / was dismissed.
export async function signInWithGoogle(): Promise<string | null> {
  if (typeof window === "undefined") return null;
  const clientId = await getClientId();
  if (!clientId) return null;

  try {
    await loadGis();
  } catch {
    return null;
  }

  const google = (window as any).google;
  if (!google?.accounts?.id) return null;

  return new Promise<string | null>((resolve) => {
    let settled = false;
    const done = (v: string | null) => {
      if (!settled) {
        settled = true;
        resolve(v);
      }
    };

    try {
      google.accounts.id.initialize({
        client_id: clientId,
        auto_select: false,
        cancel_on_tap_outside: true,
        callback: (resp: { credential?: string }) =>
          done(resp?.credential ?? null),
      });

      google.accounts.id.prompt((notification: any) => {
        // If One Tap can't be shown (cooldown / suppressed / skipped),
        // don't hang — resolve null so the caller can show a fallback.
        const notShown =
          notification?.isNotDisplayed?.() || notification?.isSkippedMoment?.();
        if (notShown) done(null);
      });
    } catch {
      done(null);
    }

    // Safety timeout so a stuck prompt never leaves the button spinning.
    setTimeout(() => done(null), 60_000);
  });
}
